import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const PayPalAdmin = () => {
  const [paypalData, setPaypalData] = useState({
    clientId: "",
    clientSecret: "",
    mode: "sandbox",
    currency: "USD",
    buttonStyles: {
      layout: "vertical",
      color: "blue",
      shape: "rect",
      label: "paypal"
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [error, setError] = useState(null);
  const [showSecret, setShowSecret] = useState(false);

  // Inline styles
  const styles = {
    container: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 0 20px rgba(0,0,0,0.05)',
      padding: '30px',
      maxWidth: '1200px',
      margin: '20px auto',
    },
    header: {
      borderBottom: '1px solid #eaeaea',
      paddingBottom: '15px',
      marginBottom: '25px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    heading: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#333',
      margin: '0',
    },
    section: {
      backgroundColor: '#f9f9f9',
      borderRadius: '6px',
      padding: '20px',
      marginBottom: '25px',
      borderLeft: '4px solid #169BD7', // PayPal blue color
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '500',
      marginBottom: '20px',
      color: '#444',
    },
    formGroup: {
      marginBottom: '20px',
    },
    formRow: {
      display: 'flex',
      flexWrap: 'wrap',
      margin: '0 -10px 15px -10px',
      alignItems: 'center',
    },
    formCol: {
      flex: '1 1 250px',
      padding: '0 10px',
    },
    input: {
      width: '100%',
      padding: '10px 15px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px',
      transition: 'border-color 0.2s ease',
    },
    textarea: {
      width: '100%',
      padding: '10px 15px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px',
      minHeight: '100px',
      resize: 'vertical',
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500',
      fontSize: '14px',
      color: '#555',
    },
    select: {
      width: '100%',
      padding: '10px 15px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px',
      backgroundColor: '#fff',
      cursor: 'pointer',
    },
    actionButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '15px',
      marginTop: '30px',
    },
    saveButton: {
      backgroundColor: '#28a745',
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '500',
      transition: 'background-color 0.2s ease',
    },
    resetButton: {
      backgroundColor: '#ffc107',
      color: '#333',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '500',
      transition: 'background-color 0.2s ease',
    },
    testButton: {
      backgroundColor: '#169BD7', // PayPal blue
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '500',
      transition: 'background-color 0.2s ease',
    },
    switch: {
      position: 'relative',
      display: 'inline-block',
      width: '60px',
      height: '34px',
    },
    switchInput: {
      opacity: 0,
      width: 0,
      height: 0,
    },
    slider: {
      position: 'absolute',
      cursor: 'pointer',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#ccc',
      transition: '.4s',
      borderRadius: '34px',
    },
    sliderBefore: {
      position: 'absolute',
      content: '',
      height: '26px',
      width: '26px',
      left: '4px',
      bottom: '4px',
      backgroundColor: 'white',
      transition: '.4s',
      borderRadius: '50%',
      transform: 'translateX(0)',
    },
    sliderActive: {
      backgroundColor: '#2196F3',
    },
    sliderBeforeActive: {
      transform: 'translateX(26px)',
    },
    buttonStylesPreview: {
      border: '1px solid #ddd',
      borderRadius: '4px',
      padding: '15px',
      textAlign: 'center',
      marginTop: '15px',
      backgroundColor: '#fff'
    },
    paypalButton: {
      backgroundColor: '#169BD7',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '4px',
      cursor: 'not-allowed',
      fontSize: '16px',
      display: 'inline-block',
      fontWeight: 'bold',
      textAlign: 'center',
      width: '200px',
      position: 'relative'
    },
    inputWithButton: {
      position: 'relative',
      display: 'flex',
    },
    passwordToggle: {
      position: 'absolute',
      right: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: '#555',
    },
    loading: {
      textAlign: 'center',
      padding: '50px 0',
      fontSize: '18px',
      color: '#777',
    },
    error: {
      backgroundColor: '#f8d7da',
      color: '#721c24',
      padding: '15px',
      borderRadius: '4px',
      marginBottom: '20px',
      fontSize: '16px',
    },
    statusBadge: {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '50px',
      fontSize: '14px',
      fontWeight: '500',
      marginBottom: '20px',
    },
    statusActive: {
      backgroundColor: '#d4edda',
      color: '#155724',
    },
    statusInactive: {
      backgroundColor: '#f8d7da',
      color: '#721c24',
    },
    helpText: {
      fontSize: '13px',
      color: '#6c757d',
      marginTop: '5px',
    }
  };

  useEffect(() => {
    fetchPayPalData();
  }, []);

  const fetchPayPalData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://server.enjoywiki.com/youtubeai-may/api/paypal-settings');
      if (response.data.success) {
        setPaypalData(response.data.data);
      } else {
        setError('Failed to fetch PayPal data');
      }
    } catch (err) {
      console.error("Error fetching PayPal data:", err);
      setError('Error fetching PayPal data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPaypalData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setPaypalData(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  const handleToggleChange = (name) => {
    setPaypalData(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const handleButtonStyleChange = (e) => {
    const { name, value } = e.target;
    setPaypalData(prev => ({
      ...prev,
      buttonStyles: {
        ...prev.buttonStyles,
        [name]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await axios.put('https://server.enjoywiki.com/youtubeai-may/api/paypal-settings', paypalData);
      if (response.data.success) {
        toast.success('PayPal settings updated successfully');
      } else {
        toast.error('Failed to update PayPal settings');
      }
    } catch (err) {
      console.error("Error updating PayPal settings:", err);
      toast.error('Error updating PayPal settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all PayPal settings to default?')) {
      try {
        setSaving(true);
        const response = await axios.post('https://server.enjoywiki.com/youtubeai-may/api/paypal-settings/reset');
        if (response.data.success) {
          toast.success('PayPal settings reset successfully');
          fetchPayPalData();
        } else {
          toast.error('Failed to reset PayPal settings');
        }
      } catch (err) {
        console.error("Error resetting PayPal settings:", err);
        toast.error('Error resetting PayPal settings');
      } finally {
        setSaving(false);
      }
    }
  };

  const testConnection = async () => {
    try {
      setTestingConnection(true);
      const response = await axios.post('https://server.enjoywiki.com/youtubeai-may/api/paypal-settings/test-connection', {
        clientId: paypalData.clientId,
        clientSecret: paypalData.clientSecret,
        mode: paypalData.mode
      });
      
      if (response.data.success) {
        toast.success('PayPal connection test successful!');
      } else {
        toast.error('PayPal connection test failed');
      }
    } catch (err) {
      console.error("Error testing PayPal connection:", err);
      toast.error(`PayPal connection test failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setTestingConnection(false);
    }
  };

  const renderPayPalButtonPreview = () => {
    const { color, shape, label } = paypalData.buttonStyles;
    
    // Dynamic styles based on button settings
    const buttonStyle = {
      ...styles.paypalButton,
      backgroundColor: color === 'gold' ? '#FFC439' : 
                       color === 'blue' ? '#169BD7' : 
                       color === 'silver' ? '#EEEEEE' : 
                       color === 'white' ? '#FFFFFF' : 
                       color === 'black' ? '#000000' : '#169BD7',
      color: ['white', 'black'].includes(color) ? 
             (color === 'white' ? '#000000' : '#FFFFFF') : '#FFFFFF',
      borderRadius: shape === 'pill' ? '50px' : shape === 'rect' ? '4px' : '0',
    };

    // Label text
    const labelText = label === 'paypal' ? 'PayPal' :
                      label === 'checkout' ? 'Checkout' :
                      label === 'buynow' ? 'Buy Now' :
                      label === 'pay' ? 'Pay with PayPal' : 'PayPal';

    return (
      <div style={styles.buttonStylesPreview}>
        <p style={{ marginBottom: '10px', fontSize: '14px' }}>Button Preview:</p>
        <div style={buttonStyle}>
          {labelText}
        </div>
        <p style={{ marginTop: '10px', fontSize: '12px', color: '#777' }}>
          (Preview only - actual button will be rendered by PayPal SDK)
        </p>
      </div>
    );
  };

  if (loading) return <div style={styles.loading}>
    <div className="spinner-border text-primary" role="status">
      <span className="sr-only"></span>
    </div>
    <p>Loading PayPal settings...</p>
  </div>;
  
  if (error) return <div style={styles.error}>
    <i className="fas fa-exclamation-circle mr-2"></i>
    Error: {error}
  </div>;

  // Custom slider render function
  const renderSwitch = (isChecked) => {
    const sliderStyle = {
      ...styles.slider,
      ...(isChecked ? styles.sliderActive : {})
    };
    
    const sliderBeforeStyle = {
      ...styles.sliderBefore,
      ...(isChecked ? styles.sliderBeforeActive : {})
    };
    
    return (
      <div style={styles.switch}>
        <input
          type="checkbox"
          style={styles.switchInput}
          checked={isChecked}
          readOnly
        />
        <span style={sliderStyle}>
          <div style={sliderBeforeStyle}></div>
        </span>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.heading}>PayPal Integration Settings</h2>
        <div style={{
          ...styles.statusBadge,
          ...(paypalData.enablePayPal ? styles.statusActive : styles.statusInactive)
        }}>
          {paypalData.enablePayPal ? 'Active' : 'Inactive'}
        </div>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>General Settings</h3>
          
          <div style={styles.formGroup}>
            <div style={styles.formRow}>
              <div style={styles.formCol}>
                <label style={styles.label}>Enable PayPal Payments:</label>
                <div onClick={() => handleToggleChange('enablePayPal')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  {renderSwitch(paypalData.enablePayPal)}
                  <span style={{ marginLeft: '10px' }}>
                    {paypalData.enablePayPal ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Mode:</label>
            <select 
              name="mode" 
              value={paypalData.mode} 
              onChange={handleChange}
              style={styles.select}
            >
              <option value="sandbox">Sandbox (Testing)</option>
              <option value="live">Live (Production)</option>
            </select>
            <p style={styles.helpText}>
              Use Sandbox mode for testing, and Live mode for real payments.
            </p>
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Client ID:</label>
            <input 
              type="text" 
              name="clientId" 
              value={paypalData.clientId} 
              onChange={handleChange}
              style={styles.input}
              placeholder="Enter PayPal Client ID"
            />
            <p style={styles.helpText}>
              Available in your PayPal Developer Dashboard
            </p>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Currency:</label>
            <select 
              name="currency" 
              value={paypalData.currency} 
              onChange={handleChange}
              style={styles.select}
            >
              <option value="USD">US Dollar (USD)</option>
              <option value="EUR">Euro (EUR)</option>
              <option value="GBP">British Pound (GBP)</option>
              <option value="CAD">Canadian Dollar (CAD)</option>
              
            </select>
          </div>
          
          {/* <button 
            type="button" 
            onClick={testConnection}
            style={styles.testButton}
            disabled={testingConnection || !paypalData.clientId || !paypalData.clientSecret}
          >
            {testingConnection ? 'Testing Connection...' : 'Test PayPal Connection'}
          </button> */}
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Button Appearance</h3>
          
          <div style={styles.formRow}>
            <div style={styles.formCol}>
              <label style={styles.label}>Layout:</label>
              <select 
                name="layout" 
                value={paypalData.buttonStyles.layout} 
                onChange={handleButtonStyleChange}
                style={styles.select}
              >
                <option value="vertical">Vertical</option>
                <option value="horizontal">Horizontal</option>
              </select>
            </div>
            
            <div style={styles.formCol}>
              <label style={styles.label}>Color:</label>
              <select 
                name="color" 
                value={paypalData.buttonStyles.color} 
                onChange={handleButtonStyleChange}
                style={styles.select}
              >
                <option value="gold">Gold</option>
                <option value="blue">Blue</option>
                <option value="silver">Silver</option>
                <option value="white">White</option>
                <option value="black">Black</option>
              </select>
            </div>
          </div>
          
          <div style={styles.formRow}>
            <div style={styles.formCol}>
              <label style={styles.label}>Shape:</label>
              <select 
                name="shape" 
                value={paypalData.buttonStyles.shape} 
                onChange={handleButtonStyleChange}
                style={styles.select}
              >
                <option value="rect">Rectangle</option>
                <option value="pill">Pill</option>
              </select>
            </div>
            
            <div style={styles.formCol}>
              <label style={styles.label}>Label:</label>
              <select 
                name="label" 
                value={paypalData.buttonStyles.label} 
                onChange={handleButtonStyleChange}
                style={styles.select}
              >
                <option value="paypal">PayPal</option>
                <option value="checkout">Checkout</option>
                <option value="buynow">Buy Now</option>
                <option value="pay">Pay with PayPal</option>
              </select>
            </div>
          </div>
          
          {renderPayPalButtonPreview()}
        </div>

        <div style={styles.actionButtons}>
          <button 
            type="button" 
            onClick={handleReset}
            style={styles.resetButton}
            disabled={saving}
          >
            {saving ? 'Processing...' : 'Reset to Default'}
          </button>
          <button 
            type="submit" 
            style={styles.saveButton} 
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PayPalAdmin;